import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import React from 'react';

import { useLazyGetTransactionsCSVQuery } from '@/api/v1';
import { ApplicationDialog } from '@/components/organisms/ApplicationDialog';
import { ApplicationModal } from '@/components/organisms/ApplicationModal';
import { useTranslator } from '@/hooks/useTranslator';
import { useTypedSearchParams } from '@/hooks/useTypedSearchParams';
import { TransactionsFilters } from '@/Screens/Dashboard/Activity/TransactionsFilters';
import { GetTransactionsFilters } from '@/types/getTransactionsFilters';
import { triggerDownload } from '@/utility/triggerDownload';

interface Props {
  readonly filtersCount: number;
}

export const ActivitySearchBar: React.FC<Props> = (props: Props): React.ReactElement => {
  const { filtersCount } = props;

  const [filtersModalOpen, setFiltersModalOpen] = React.useState<boolean>(false);
  const [filters, setFilters] = useTypedSearchParams(GetTransactionsFilters.initial());
  const [downloadStatement] = useLazyGetTransactionsCSVQuery();

  const tr = useTranslator();

  const onDownloadStatementClick = React.useCallback(async (): Promise<void> => {
    try {
      const { data } = await downloadStatement(filters);
      if (data?.filename && data?.data) {
        triggerDownload(data.data, data.filename);
      }
    } catch (error) {
      console.error('Error downloading CSV:', error);
    }
  }, [downloadStatement, filters]);

  const onFiltersModalClose = React.useCallback((): void => {
    setFiltersModalOpen(false);
  }, []);

  const openFiltersModal = React.useCallback((): void => {
    setFiltersModalOpen(true);
  }, []);

  const handleFiltersChange = React.useCallback(
    (filters: GetTransactionsFilters): void => {
      setFilters({ ...filters, page: 0 });
      setFiltersModalOpen(false);
    },
    [setFilters],
  );

  return (
    <Box display="flex" alignItems="center" justifyContent="space-between">
      <Box width={359}>{/* <SearchBox fullWidth={true} disabled={true} /> */}</Box>
      <Box display="flex" alignItems="center" gap={2} height={40}>
        <Button variant="light" size="compact" onClick={openFiltersModal}>
          {filtersCount === 0 ? tr('FILTER') : tr('FILTER_WITH_COUNT', filtersCount)}
        </Button>
        <Button variant="dark" size="compact" onClick={onDownloadStatementClick}>
          {tr('DOWNLOAD_STATEMENT')}
        </Button>
      </Box>

      {filtersModalOpen && (
        <ApplicationDialog>
          <ApplicationModal
            title={tr('FILTER')}
            description={tr('SELECT_FILTERS_THAT_APPLY')}
            onClose={onFiltersModalClose}>
            <TransactionsFilters filters={filters} onChange={handleFiltersChange} />
          </ApplicationModal>
        </ApplicationDialog>
      )}
    </Box>
  );
};
